import React, { Fragment, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  base64ToBlob,
  Button,
  clsx,
  Heading,
  SvgIcon,
  Typography,
  useBoolean,
  useDidMount,
  useRerender,
} from "@gemlightbox/core-kit";

import { MediaType } from "src/models";
import { useStores } from "src/hooks";
import { editMediaStore } from "../edit-media.store";
import { ScaleSelect } from "./scale-select";

import { ReactComponent as BackSVG } from "src/external-ts/assets/images/arrow-left-grey.svg";
import { ReactComponent as NewBadgeSVG } from "src/external-ts/assets/images/badges/new-badge.svg";
import { ReactComponent as BetaBadgeSVG } from "src/external-ts/assets/images/badges/beta-badge.svg";
import { ReactComponent as UndoSVG } from "src/external-ts/assets/images/undo-arrow-grey.svg";
import { ReactComponent as GenerativeAISVG } from "src/external-ts/assets/images/edit-media/generative-ai/generative-ai-icon.svg";
import { ReactComponent as AiRetouchSVG } from "src/external-ts/assets/images/edit-media/ai-retouch-icon.svg";
import { ReactComponent as RemoveBackgroundSVG } from "src/external-ts/assets/images/edit-media/background-icon.svg";
import { ReactComponent as DustRemoveSVG } from "src/external-ts/assets/images/edit-media/dust-remove-icon.svg";
import { ReactComponent as RulerSVG } from "src/external-ts/assets/images/edit-media/ruler-grey.svg";
import styles from "./edit-controls-bar.module.css";
import { MuiSwitch, ProUserUpgradeModal } from "src/external-ts/components";
import { useParams } from "react-router-dom";
// import { GenerativeAiModal } from "src/containers/products/modals/generative-ai-modals";
import { generativeAiStore } from "src/containers/products/modals/generative-ai-modals/generative-ai-store";
import { pushDataLayerEvent, pushUpdateContextPropertiesEvent } from "src/utils";

export type EditControlsBarProps = {
  onGoBack: VoidFunction;
};

export const EditControlsBar: React.FC<EditControlsBarProps> = observer(
  ({ onGoBack }: EditControlsBarProps) => {
    const rerender = useRerender();
    const { mediaId: initMediaId } = useParams<{ mediaId: string }>();
    const [mediaId, setMediaId] = useState(initMediaId);

    const {
      localeStore,
      notificationStore,
      mediaStore,
      subscriptionsStore,
      userStore,
      tooltipStore,
    } = useStores();

    let { userInfoSubscription, setGemStudioCredits, autoGemStudio, setAutoGemStudio } =
      generativeAiStore;

    const {
      renderer,
      isInMeasurement,
      isInCrop,
      isInResize,
      isInBackground,
      rulersEnabled,
      metaData,
      orignalUrl,
    } = editMediaStore;
    const isDisableFeatures = isInCrop || isInMeasurement || isInResize;
    // NOTE: Dust removal is temporarily not available for users.
    const isRemoveDustEnabled = false;
    const [proUserUpgradeContent, setProUserUpgradeContent] = useState("");
    const proUserUpgradeBool = useBoolean(false);
    const generativeAiModalBool = useBoolean(false);
    const generativeAiEnable = useBoolean(false);
    const [genAiEnabledInfo, setGenAiEnabledInfo] = useState({});

    const handleConfirmClose = () => {
      const proceedClose = () => {
        // NOTE: `canUndo/canRedo` depends on `commands.events.block()`.
        // In Crop/Resize/Measurements we intentionally block commands,
        // but exiting editor should still warn about existing history.
        if (!renderer.commands.historyLength) {
          return onGoBack();
        }

        notificationStore.open({
          title: localeStore.t('["edit-media"]["cancel-edit"].title'),
          message: localeStore.t('["edit-media"]["cancel-edit"].message'),
          confirmText: localeStore.t("common.buttons.confirm"),
          cancelText: localeStore.t("common.buttons.cancel"),
          dataCy: "close-edit-media-notification",
          onOk: onGoBack,
        });
      };

      editMediaStore.proceedWithTabBlock(proceedClose);
    };

    const handleRemoveBackground = () => {
      if (
        !userStore.isSubscribed &&
        (userStore.userUsage?.countBackgroundRemoval ?? 0) >=
          subscriptionsStore.limits.count_removal_background
      ) {
        setProUserUpgradeContent(localeStore.t('common["pro-user"]["remove-bg-upgrade-text"]'));
        proUserUpgradeBool.setTruthy();
        return;
      }

      editMediaStore.removeBG({ mediaId: mediaId ? Number(mediaId) : 0 });
    };

    const handleRetouchAI = () => {
      if (
        !userStore.isSubscribed &&
        (userStore.userUsage?.countRetouch ?? 0) >= subscriptionsStore.limits.availableRetouch
      ) {
        setProUserUpgradeContent(localeStore.t('common["pro-user"]["ai-retouch-upgrade-text"]'));
        proUserUpgradeBool.setTruthy();
        return;
      }

      editMediaStore.retouchAI({ mediaId });
    };

    const handleRemoveDust = () => {
      if (
        !userStore.isSubscribed &&
        (userStore.userUsage?.countDustRemoval ?? 0) >= subscriptionsStore.limits.count_remove_dust
      ) {
        setProUserUpgradeContent(localeStore.t('common["pro-user"]["remove-dust-upgrade-text"]'));
        proUserUpgradeBool.setTruthy();
        return;
      }

      editMediaStore.removeDust({ mediaId });
    };

    const handleGenerativeAI = () => {
      const product = editMediaStore.mediaShortModel?.product;
      pushDataLayerEvent({
        event: "click:gemstudio:open",
        event_params: { product_id: product?._id, media_id: mediaId, sku: product?.title },
      });
      generativeAiModalBool.setTruthy();
    };

    const handleUndo = () => renderer.commands.undo();
    const handleRedo = () => renderer.commands.redo();

    const handleRulersChange = (checked: boolean) => editMediaStore.setRulersEnabled(checked);

    const handleSaveBtnLeave = () => {
      if (editMediaStore.isGenerativeAI && !renderer.commands.canUndo) {
        tooltipStore.close();
      }
    };

    const handleSaveBtnEnter = (e: any) => {
      if (editMediaStore.isGenerativeAI && !renderer.commands.canUndo) {
        tooltipStore.open(
          <Fragment>
            <Typography size="extraSmall">
              {localeStore.t('["edit-media"]["generative-ai"].save_btn_tooltip')}
            </Typography>
          </Fragment>,
          {
            appearance: "secondary",
            position: "bottomRight",
            target: e.currentTarget,
            withAngle: true,
            onPointerLeave: () => tooltipStore.close(),
          },
        );
      }
    };

    const handleSave = () => {
      const mediaShortModel = editMediaStore.mediaShortModel;
      if (!mediaShortModel || !mediaShortModel?.file) return;

      const base64 = editMediaStore.renderer.export.getCanvasCast().toDataURL();
      const dataUrl = base64.split(",")[1];
      const blob = base64ToBlob(dataUrl, "image/png");
      const formData = new FormData();
      const fileName = mediaShortModel.file.filename.split(".").shift() + ".png";
      formData.append("files", blob, fileName);

      notificationStore.open({
        title: localeStore.t('["edit-media"].save.title'),
        confirmText: localeStore.t('["edit-media"].save["ok-text"]'),
        cancelText: localeStore.t('["edit-media"].save["cancel-text"]'),
        confirmAppearance: "primary",
        onOk: async () => {
          formData.append("type", MediaType.image);
          formData.append("metaData", JSON.stringify({ ...metaData }));
          const result = await mediaStore.updateMedia(mediaId ? +mediaId : 0, formData);
          if (result.success) {
            onGoBack();
          }
        },
        onCancel: async () => {
          formData.append("types", MediaType.image);
          formData.append("metaData", JSON.stringify({ ...metaData }));
          if (mediaShortModel.product && mediaShortModel.product.mediaCount < 100) {
            formData.append("sku", mediaShortModel.product._id);
          }
          const result = await mediaStore.uploadMedia(formData);
          if (result.success) {
            onGoBack();
          }
        },
      });
    };

    const handleMouseEnter =
      (
        btnType:
          | "generative-ai"
          | "remove-background"
          | "ai-retouch"
          | "remove-dust"
          | "rulers"
          | "undo"
          | "redo",
      ) =>
      (event: React.PointerEvent<HTMLElement>) => {
        const isDisabled = Boolean((event.currentTarget as HTMLButtonElement).disabled);
        let tooltipText = "";
        let className = styles.tooltipBtn;

        const getTooltipText = (
          enabledTextKey: string,
          disabledTextKey: string,
          usageInfo: string,
        ) => {
          return isDisabled
            ? localeStore.t(disabledTextKey as any)
            : `${localeStore.t(enabledTextKey as any)}${usageInfo}`;
        };

        switch (btnType) {
          case "remove-background":
            tooltipText = getTooltipText(
              '["edit-media"]["edit-controls-bar"]["remove-background"]',
              '["edit-media"]["edit-controls-bar"]["remove-background-tag"]',
              generateUsageInfo(
                subscriptionsStore.limits.count_removal_background,
                userStore.userUsage?.countBackgroundRemoval,
              ),
            );
            isDisabled && (className = "");
            break;

          case "ai-retouch":
            tooltipText = getTooltipText(
              '["edit-media"]["edit-controls-bar"]["ai-retouch"]',
              '["edit-media"]["edit-controls-bar"]["ai-retouch-tag"]',
              generateUsageInfo(
                subscriptionsStore.limits.availableRetouch,
                userStore.userUsage?.countRetouch,
              ),
            );
            isDisabled && (className = "");
            break;

          case "remove-dust":
            tooltipText = getTooltipText(
              '["edit-media"]["edit-controls-bar"]["remove-dust"]',
              '["edit-media"]["edit-controls-bar"]["remove-dust-tag"]',
              generateUsageInfo(
                subscriptionsStore.limits.count_remove_dust,
                userStore.userUsage?.countDustRemoval,
              ),
            );
            isDisabled && (className = "");
            break;

          case "generative-ai":
            tooltipText = localeStore.t('["edit-media"]["generative-ai"].name');
            className = styles.tooltipBtn2;
            break;

          case "rulers":
            tooltipText = localeStore.t('["edit-media"]["edit-controls-bar"].rulers');
            className = styles.tooltipBtn2;
            break;

          case "undo":
            tooltipText = localeStore.t("common.buttons.undo");
            className = styles.tooltipBtn2;
            break;

          case "redo":
            tooltipText = localeStore.t("common.buttons.redo");
            className = styles.tooltipBtn2;
            break;

          default:
            break;
        }

        tooltipStore.open(<Typography size="extraSmall">{tooltipText}</Typography>, {
          target: event.currentTarget,
          appearance: "secondary",
          position: "bottom",
          withAngle: true,
          className: clsx(styles.tooltipWin, className),
        });
      };

    const handleMouseLeave = () => tooltipStore.close();

    const generateUsageInfo = (limitsCount: number, usedCount: number | undefined | null) => {
      if (!userStore.isSubscribed) {
        return `(${Math.max(0, limitsCount - (usedCount ?? 0))}/${limitsCount})`;
      }
      return "";
    };

    useDidMount(() => {
      return renderer.commands.events.on("history", rerender);
    });

    useEffect(() => {
      findUserInfoSubscription();
      let urlObj = new URL(location.href);
      const gs = urlObj?.searchParams?.get("gemStudio");
      if (gs && gs && !["undefined", "null"].includes(gs)) {
        generativeAiModalBool.setTruthy();
        setAutoGemStudio(true);
      }
    }, []);

    async function findUserInfoSubscription() {
      const data = await userInfoSubscription();
      if (data?.organization?.gemStudioCredits) {
        setGemStudioCredits(data?.organization?.gemStudioCredits);
      }
      setGenAiEnabledInfo(data?.organization?.additionalInfo);
      if (data?.organization?.additionalInfo?.genAiEnabled) {
        generativeAiEnable.setTruthy();
      } else {
        generativeAiEnable.setFalsy();
      }

      pushUpdateContextPropertiesEvent({
        "gemstudio:enabled": !!data?.organization?.additionalInfo?.genAiEnabled,
        "gemstudio:model_image_pro:enabled":
          !!data?.organization?.additionalInfo?.genAiModelImageEnabled,
        "gemstudio:change_gold_color:enabled":
          !!data?.organization?.additionalInfo?.genAiChangeMetalMaterialEnabled,
        "gemstudio:lifestyle_image:enabled":
          !!data?.organization?.additionalInfo?.genAiLifestyleImageEnabled,
        "gemstudio:credits:available": data?.organization?.gemStudioCredits,
      });
    }

    return (
      <div className={styles.controlsBar}>
        <div className={styles.titleWrapper}>
          <Button
            appearance="secondaryOutlined"
            onClick={handleConfirmClose}
            data-cy="close-button"
          >
            <SvgIcon icon={BackSVG} />
          </Button>
          <Heading tag="h2" color="textSecondary">
            {localeStore.t('["edit-media"].title')}
          </Heading>
        </div>
        <div className={styles.controlsWrapper}>
          {/* {generativeAiEnable.value && (
            <Button
              className={styles.removeBGButton}
              style={{ marginRight: "4px" }}
              appearance="primaryOutlined"
              onClick={handleGenerativeAI}
              data-cy="generative-ai-button"
              onPointerEnter={handleMouseEnter("generative-ai")}
              onPointerLeave={handleMouseLeave}
            >
              <SvgIcon className={styles.newBadge} icon={BetaBadgeSVG} size={[28, 12]} />
              <SvgIcon icon={GenerativeAISVG} />
              <span>{localeStore.t('["edit-media"]["generative-ai"].name')}</span>
            </Button>
          )} */}

          {!isDisableFeatures && (
            <Button
              className={styles.removeBGButton}
              appearance="primaryOutlined"
              onClick={handleRemoveBackground}
              data-cy="remove-background-button"
              disabled={
                !!metaData?.isAiBackgroundRemoval ||
                !!metaData?.isAiRetouch ||
                !!metaData?.isEclipseMode
              }
              onPointerEnter={handleMouseEnter("remove-background")}
              onPointerLeave={handleMouseLeave}
            >
              {/* <SvgIcon className={styles.newBadge} icon={NewBadgeSVG} size={[28, 12]} /> */}
              <SvgIcon icon={RemoveBackgroundSVG} />
              <span>
                {localeStore.t('["edit-media"]["edit-controls-bar"]["remove-background"]')}
                {generateUsageInfo(
                  subscriptionsStore.limits.count_removal_background,
                  userStore.userUsage?.countBackgroundRemoval,
                )}
              </span>
            </Button>
          )}

          {!isDisableFeatures && (
            <Button
              className={styles.retouchAIButton}
              appearance="primaryOutlined"
              onClick={handleRetouchAI}
              data-cy="ai-retouch-button"
              disabled={!!metaData?.isAiRetouch || !!metaData?.isEclipseMode}
              onPointerEnter={handleMouseEnter("ai-retouch")}
              onPointerLeave={handleMouseLeave}
            >
              {/* <SvgIcon className={styles.newBadge} icon={NewBadgeSVG} size={[28, 12]} /> */}
              <SvgIcon icon={AiRetouchSVG} />
              <span>
                {localeStore.t('["edit-media"]["edit-controls-bar"]["ai-retouch"]')}
                {generateUsageInfo(
                  subscriptionsStore.limits.availableRetouch,
                  userStore.userUsage?.countRetouch,
                )}
              </span>
            </Button>
          )}

          {!isDisableFeatures && isRemoveDustEnabled && (
            <Button
              className={styles.dustButton}
              appearance="primaryOutlined"
              onClick={handleRemoveDust}
              data-cy="remove-dust-button"
              disabled={
                !!metaData?.isAiRemoveDust || !!metaData?.isAiRetouch || !!metaData?.isEclipseMode
              }
              onPointerEnter={handleMouseEnter("remove-dust")}
              onPointerLeave={handleMouseLeave}
            >
              {/* <SvgIcon className={styles.newBadge} icon={NewBadgeSVG} size={[28, 12]} /> */}
              <SvgIcon icon={DustRemoveSVG} />
              <span>
                {localeStore.t('["edit-media"]["edit-controls-bar"]["remove-dust"]')}
                {generateUsageInfo(
                  subscriptionsStore.limits.count_remove_dust,
                  userStore.userUsage?.countDustRemoval,
                )}
              </span>
            </Button>
          )}

          <div className={styles.viewGroup}>
            <ScaleSelect className={styles.pctSelect} />
            <div
              className={clsx(styles.rulersControl, {
                [styles.rulersControlActive]: rulersEnabled,
              })}
              data-name="rulers-button"
              onPointerEnter={handleMouseEnter("rulers")}
              onPointerLeave={handleMouseLeave}
            >
              <SvgIcon icon={RulerSVG} />
              <span className={styles.rulersControlLabel}>
                {localeStore.t('["edit-media"]["edit-controls-bar"].rulers')}
              </span>
              <MuiSwitch
                checked={rulersEnabled}
                onChange={(_, checked) => handleRulersChange(checked)}
                aria-label={localeStore.t('["edit-media"]["edit-controls-bar"].rulers')}
              />
            </div>
          </div>
          <Button
            className={styles.undoBtn}
            appearance="primaryOutlined"
            onClick={handleUndo}
            disabled={!renderer.commands.canUndo || isDisableFeatures}
            data-name="undo-button"
            onPointerEnter={handleMouseEnter("undo")}
            onPointerLeave={handleMouseLeave}
          >
            <SvgIcon icon={UndoSVG} />
            <span>{localeStore.t("common.buttons.undo")}</span>
          </Button>
          <Button
            className={styles.redoBtn}
            appearance="primaryOutlined"
            onClick={handleRedo}
            disabled={!renderer.commands.canRedo || isDisableFeatures}
            data-name="redo-button"
            onPointerEnter={handleMouseEnter("redo")}
            onPointerLeave={handleMouseLeave}
          >
            <span>{localeStore.t("common.buttons.redo")}</span>
            <SvgIcon icon={UndoSVG} />
          </Button>

          <Button
            className={styles.saveBtn}
            onPointerEnter={handleSaveBtnEnter}
            onPointerLeave={handleSaveBtnLeave}
            onClick={handleSave}
            disabled={!renderer.commands.canUndo || isDisableFeatures}
            data-cy="save-button"
          >
            {editMediaStore.isGenerativeAI && !renderer.commands.canUndo
              ? localeStore.t("common.buttons.saved")
              : localeStore.t("common.buttons.save")}
          </Button>
        </div>

        <ProUserUpgradeModal
          visible={proUserUpgradeBool}
          onClose={proUserUpgradeBool.setFalsy}
          content={proUserUpgradeContent}
        ></ProUserUpgradeModal>

        {/* <GenerativeAiModal
          isOpen={generativeAiModalBool}
          setClose={generativeAiModalBool.setFalsy}
          setOpen={generativeAiModalBool.setTruthy}
          genAiEnabledInfo={genAiEnabledInfo}
          mediaId={mediaId}
          mediaUrl={orignalUrl}
          setMediaId={setMediaId}
        /> */}
      </div>
    );
  },
);

export default EditControlsBar;
